import { Coupon } from '@dtx-company/true-common/src/types/Coupon'
import { FIRST_MONTH_FREE } from '../constants/promoCodes'
import { Plan } from '@dtx-company/true-common/src/types/Plan'
import { PlanInterval } from '@dtx-company/true-common/src/types/PlanInterval'
import { getDiscountPercentForPlanFromPromo } from '../utils/getDiscountPercentForPlanFromPromo'
import { getPercentText } from '@dtx-company/app/shared/common/src/utils/getPercentText'
import { getPlanIntervalName } from '../utils/getPlanIntervalName'
import { getPlanTypeName } from '../utils/getPlanTypeName'
import { getPriceText } from '../utils/getPriceText'
import { promoAppliesToSelectedPlan } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/utils/promoAppliesToSelectedPlan'
import { useId } from 'react'
import { Box, Stack, Typography } from '@dtx-company/design-system/src'
import { Radio } from '@dtx-company/design-system/src/internal-components/Radio/Radio'
import { ChipBadge } from '@dtx-company/design-system/src/components/ChipBadge/ChipBadge'

function getPlanTextParts(
  plan: Plan,
  discountPercentage?: number
): {
  priceText: string
  discountedPriceText?: string
  priceIntervalText: string
} {
  const planPriceInCents = plan.startingPriceInCents ?? (plan.priceInCents || 0)
  const discountMultiplier = discountPercentage ? 1 - discountPercentage : 1
  if (plan.interval === PlanInterval.YEARLY) {
    const yearlyPrice = getPriceText({
      priceInCents: planPriceInCents * discountMultiplier
    })
    return {
      priceText: getPriceText({ priceInCents: planPriceInCents / 12 }),
      ...(discountPercentage && {
        discountedPriceText: getPriceText({
          priceInCents: (planPriceInCents / 12) * discountMultiplier
        })
      }),
      priceIntervalText: `per month (${yearlyPrice} billed annually)`
    }
  }
  if (plan.interval === PlanInterval.MONTHLY) {
    return {
      priceText: getPriceText({ priceInCents: planPriceInCents }),
      ...(discountPercentage && {
        discountedPriceText: getPriceText({
          priceInCents: planPriceInCents * discountMultiplier
        })
      }),
      priceIntervalText: `per month (billed monthly)`
    }
  }
  // If there's some new interval just toss all the standard boilerplate text into
  // the interval text line
  return {
    priceText: '',
    priceIntervalText: getPriceText({
      priceInCents: planPriceInCents,
      ...(discountPercentage && {
        discountedPriceText: getPriceText({
          priceInCents: planPriceInCents * discountMultiplier
        })
      }),
      interval: plan.interval,
      discountPercent: plan.discountPercent
    })
  }
}

type PlanPickerItemBadgeProps = PlanPickerItemProps

function PlanPickerItemBadge(props: PlanPickerItemBadgeProps): JSX.Element {
  if (props.currentPlan) {
    const currentType = props.currentPlan.type
    const currentInterval = props.currentPlan.interval
    const planMatchingCurrent = props.plans.find(plan => {
      return plan.type === currentType && plan.interval === currentInterval
    })
    if (planMatchingCurrent) {
      if (props.plan.id === planMatchingCurrent.id) {
        return (
          <ChipBadge
            size="small"
            label={
              !props.isSinglePlan && planMatchingCurrent.discountPercent
                ? `You are saving ${getPercentText(planMatchingCurrent.discountPercent)}`
                : 'Current'
            }
          />
        )
      }
    }
  }

  if (props.plan.discountPercent) {
    return (
      <ChipBadge
        size="small"
        color="success"
        label={props.badge ? props.badge : `Save ${getPercentText(props.plan.discountPercent)}`}
      />
    )
  }

  if (props.badge) {
    return <ChipBadge size="small" label={props.badge} />
  }

  return <></>
}

interface PlanPickerItemProps extends PlanPickerProps {
  guid: string
  plan: Plan
  isSinglePlan: boolean
  discountPercent?: number
  badge?: string
}

function PlanPickerItem(props: PlanPickerItemProps): JSX.Element {
  const isCurrent =
    props.currentPlan &&
    props.currentPlan.type === props.plan.type &&
    props.currentPlan.interval === props.plan.interval

  const { badge, ...rest } = props
  const discount = !isCurrent ? props?.discountPercent : undefined
  const textParts = getPlanTextParts(props.plan, discount)
  const primaryText = textParts.discountedPriceText
    ? ' ' + textParts.discountedPriceText
    : textParts.priceText
  const isSelected =
    props.selectedPlan.type === props.plan.type &&
    props.selectedPlan.interval === props.plan.interval

  const priceSection = (() => {
    if (textParts.discountedPriceText) {
      // If discount is of 100% display the price cross out (line-through)
      // And do not display the discounted price `primaryText`
      if (props.discountPercent == 1) {
        return (
          <Typography
            component="span"
            variant="subtitleSmall"
            sx={{ textDecoration: 'line-through' }}
          >
            {textParts.priceText}
          </Typography>
        )
      }

      return (
        <Typography component="span" variant="bodySmall" sx={{ textDecoration: 'line-through' }}>
          {textParts.priceText}
        </Typography>
      )
    }

    return (
      <Typography component="span" variant="subtitleSmall">
        {primaryText}
      </Typography>
    )
  })()

  return (
    <Radio
      id={`plan_${props.plan.id}_${props.guid}`}
      name={`plan_${props.guid}`}
      checked={isSelected}
      disabled={Boolean(isCurrent)}
      onChange={() => props.onSelectedPlanChange(props.plan)}
      label={
        <Stack sx={{ flex: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Typography component="span" variant="subtitleSmall">
              {props.plan.type && getPlanTypeName(props.plan.type)}{' '}
              <Typography component="span" variant="bodySmall" color="text.secondary">
                {props.plan.interval && getPlanIntervalName(props.plan.interval)}
              </Typography>
            </Typography>
            <PlanPickerItemBadge {...rest} badge={!isCurrent ? badge : undefined} />
          </Stack>
          <Typography component="span" variant="subtitleSmall">
            {priceSection}{' '}
            <Typography component="span" variant="bodySmall" color="text.secondary">
              {textParts.priceIntervalText}
            </Typography>
          </Typography>
        </Stack>
      }
      labelSx={theme => ({
        alignItems: 'flex-start',
        margin: 0,
        width: '100%',
        padding: theme.spacing(2, 3),
        borderLeft: isSelected
          ? `${theme.spacing(1)} solid ${theme.palette.tertiary.main}`
          : `${theme.spacing(1)} solid transparent`,
        borderRadius: theme.spacing(1),
        ...(isSelected && {
          backgroundColor: theme.palette.shades.tertiary.hover
        })
      })}
    />
  )
}

interface PlanPickerProps {
  currentPlan?: Plan
  selectedPlan: Plan
  plans: Plan[]
  onSelectedPlanChange: (value: Plan) => void
  badgeText?: string[]
  promo: Partial<Coupon>
}

/**
 * Creates a picker for selecting from a list of plans.
 *
 * Typically will receive a monthly and annual plan that complement each other
 * but can receive any number of plans.
 */
export function PlanPicker(props: PlanPickerProps): JSX.Element {
  const guid = useId()
  const isSinglePlan = props.plans.length === 1
  const { badgeText: _, ...rest } = props

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
      {props.plans.map(plan => {
        const promoAppliesToPlan = promoAppliesToSelectedPlan(props.promo, plan)
        const promoDiscountPercent = getDiscountPercentForPlanFromPromo(props.promo, plan)

        // Display 'First month free' badge if experiment is active and variant is 'free-month',
        // the promo applies to the current plan and the promo code is FIRST_MONTH_FREE
        const badge =
          promoAppliesToPlan && props.promo.id === FIRST_MONTH_FREE ? 'First month free' : ''

        return (
          <PlanPickerItem
            key={plan.id}
            plan={plan}
            guid={guid || ''}
            isSinglePlan={isSinglePlan}
            discountPercent={promoDiscountPercent}
            badge={badge}
            {...rest}
          />
        )
      })}
    </Box>
  )
}
